import * as z from "zod";
import { erpInvoiceStateDto } from "../erp/invoices";
import {
  anyOfFilters,
  codeName,
  countByFiltersInput,
  cursorPagination,
  decimal,
  flag,
  pagination,
  uuidV7,
  version,
} from "../index";
import { aggregateSyncDto, MAX_LINE_UNITS, syncStatuses } from "../til";
import { creditNoteDto } from "./credit-note";
import {
  inboundDocumentKinds,
  inboundStatuses,
  inboundTrackingSides,
  missingItemsResolutions,
  receiptOutcomes,
} from "./enums";
import { extractionDto } from "./packing-list-extraction";
import { poReason } from "./purchase-order";
import { MAX_PURCHASE_ORDER_LINES } from "./purchase-order-line";
import { packingListLineDto, threeWayCheckDto } from "./three-way-check";

// Inbound of a PO (50-api/procurement.md §2.6; FR-M11-01, B-04, V-02, ADR-128): the supplier announces what it ships
// against a confirmed PO, with its packing list and invoice; the brand authorizes it when a line is out of tolerance.

/** Inbound by id (uuid) or by number. */
export const getInboundInput = z.object({ key: z.string().min(1).max(36) });
export type GetInboundInput = z.input<typeof getInboundInput>;

const inboundSorts = ["-updatedAt", "inboundNumber", "expectedDeliveryDate"] as const;

const inboundFilters = {
  status: z.enum(inboundStatuses).optional(),
  purchaseOrder: z.uuid().optional(),
  brand: z.uuid().optional(),
  supplier: z.uuid().optional(),
  /**
   * Waits for Logistics to type its packing list: `documents_uploaded` or `authorized` (`inboundsToCapture`), and not
   * being read by the extraction tool or waiting for its review (ADR-233).
   */
  toCapture: flag.optional(),
  /**
   * The lines the extraction tool read wait for Logistics, who confirms or corrects them (`to_review`, B-50, ADR-233):
   * the exception queue of the extraction. Only Tomorrow filters by it.
   */
  extractionToReview: flag.optional(),
  /** Received with a difference Logistics has not confirmed yet (`outcomesToResolve`, ADR-139). */
  toResolve: flag.optional(),
  /** Received with units missing that Logistics confirmed: the supplier's choice waits (`canSettleShortage`, ADR-140). */
  toSettle: flag.optional(),
  /**
   * What the supplier left undone a cadence after the last reminder of its series (B-41, B-42, ADR-245): the missing
   * units it has not chosen for, the inbound of the extra units it has not sent. Logistics' queue.
   */
  remindersOver: flag.optional(),
  /**
   * The supplier's side (ADR-170): the inbounds of the suppliers the Actor works for, those it announces, sends and
   * settles. For Tomorrow, which answers for every supplier, it takes nothing out.
   */
  asSupplier: flag.optional(),
  /** The brand's side (ADR-170): the inbounds of the brands the Actor works for, those it authorizes. */
  asBrand: flag.optional(),
  /** Part of the number, by its letters and digits (`inb12` finds `INB-0000012`, ADR-117). */
  q: z.string().trim().min(1).max(30).optional(),
};

/** The inbounds in at least one of these sets of the filters (ADR-302): the queues of the inbounds, to do. */
const anyOfInbounds = { anyOf: anyOfFilters(inboundFilters).optional() };

/** The inbounds the Actor sees: the brand sees one from its submission on, whatever the filter. */
export const listInboundsInput = z.object({
  ...inboundFilters,
  ...anyOfInbounds,
  sort: z.enum(inboundSorts).default("-updatedAt"),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListInboundsInput = z.input<typeof listInboundsInput>;

/** How many inbounds the list has with the same filters (ADR-086). */
export const countInboundsInput = z.object({ ...inboundFilters, ...anyOfInbounds });
export type CountInboundsInput = z.input<typeof countInboundsInput>;
/** The same count under several filters, in one call and one transaction (ADR-269): the queues of the home. */
export const countInboundsByFiltersInput = countByFiltersInput(countInboundsInput);
export type CountInboundsByFiltersInput = z.input<typeof countInboundsByFiltersInput>;

/** The lines of a confirmed PO an inbound can announce, with what was already announced of each. */
export const listInboundCandidatesInput = z.object({ purchaseOrderId: z.uuid() });
export type ListInboundCandidatesInput = z.input<typeof listInboundCandidatesInput>;

const text = (max: number) => z.string().trim().min(1).max(max);
const count = z.number().int().min(0).max(1_000_000);
const weight = decimal.refine((v) => Number(v) >= 0 && Number(v) < 10_000_000, "between 0 and 9999999.999");

/**
 * When the goods are ready and who hands them over: written in the draft, and after the submission until the
 * warehouse receives something, in every incoterm (B-89, ADR-244).
 */
const pickupFields = {
  readyDateForPickup: z.iso.date().nullable(),
  pickupContactName: text(200).nullable(),
  pickupPhone: text(40).nullable(),
  pickupEmail: z.email().max(320).nullable(),
};

/** An amount of the invoice, in the currency of the PO. */
const invoiceAmount = decimal.refine(
  (v) => Number(v) >= 0 && Number(v) < 1e14,
  "between 0 and the largest amount the DB holds",
);

/**
 * What the supplier writes on the inbound (the PDF of the packing list and of the invoice are documents): the
 * invoice, the packages, the pickup contact and when it ships. Destination, incoterm and currency are the PO's. On a
 * change an absent field stays as it is and `null` empties it; the submission asks for the invoice (number, date,
 * net total, VAT and total).
 */
const inboundHeader = {
  invoiceNumber: text(60).nullable().optional(),
  invoiceDate: z.iso.date().nullable().optional(),
  /** In the currency of the PO: the three-way check compares it with the quantities at the PO cost (FR-M11-06). */
  invoiceNetTotal: invoiceAmount.nullable().optional(),
  /**
   * The VAT and the total, VAT included, of the invoice (spec v0.3 §7.4.3, ADR-256): the check compares them with the
   * net total and with the VAT of the lines, and the invoices file to the ERP states the total.
   */
  invoiceVatAmount: invoiceAmount.nullable().optional(),
  invoiceTotal: invoiceAmount.nullable().optional(),
  packingListNumber: text(60).nullable().optional(),
  readyDateForPickup: pickupFields.readyDateForPickup.optional(),
  pickupContactName: pickupFields.pickupContactName.optional(),
  pickupPhone: pickupFields.pickupPhone.optional(),
  pickupEmail: pickupFields.pickupEmail.optional(),
  expectedDeliveryDate: z.iso.date().nullable().optional(),
  deliveryNumber: text(60).nullable().optional(),
  totalCartons: count.nullable().optional(),
  totalPallets: count.nullable().optional(),
  grossWeightKg: weight.nullable().optional(),
  netWeightKg: weight.nullable().optional(),
  boxDimensions: text(200).nullable().optional(),
  palletDimensions: text(200).nullable().optional(),
  contentDetails: text(2000).nullable().optional(),
  comments: text(2000).nullable().optional(),
};

/** A line of the inbound: how many units of a PO line it carries. The grid of a draft checks its cells with it. */
export const inboundLineInput = z.object({
  poLineId: z.uuid(),
  qtyDeclared: z.number().int().min(1).max(MAX_LINE_UNITS),
  /** Nothing more of this line will come: a shortage beyond the tolerance needs the brand (V-02). */
  shortShipmentAccepted: z.boolean().default(false),
});
export type InboundLineInput = z.output<typeof inboundLineInput>;

const inboundLines = z.array(inboundLineInput).min(1).max(MAX_PURCHASE_ORDER_LINES);

/** A new inbound on a confirmed PO, as a draft, with the id from the client (ADR-046). */
export const createInboundInput = z.object({
  id: uuidV7,
  purchaseOrderId: z.uuid(),
  lines: inboundLines,
  ...inboundHeader,
});
export type CreateInboundInput = z.input<typeof createInboundInput>;

/** An action on an inbound. `version` is the one the client read: an inbound changed in the meantime answers 409. */
export const inboundActionInput = z.object({ inboundId: z.uuid(), version });
export type InboundActionInput = z.input<typeof inboundActionInput>;

/** The gateway routes split the input: the id in the path, the rest in the body. */
export const inboundParams = inboundActionInput.pick({ inboundId: true });
export const inboundVersionBody = inboundActionInput.pick({ version: true });

/** PATCH, on a draft: an absent field stays as it is, `null` empties it; `lines` replaces all the lines. */
export const updateInboundInput = inboundActionInput.extend({ ...inboundHeader, lines: inboundLines.optional() });
export type UpdateInboundInput = z.input<typeof updateInboundInput>;
export const updateInboundBody = updateInboundInput.omit({ inboundId: true });

/** How many tracking numbers an inbound holds at most: one for each parcel of a shipment. */
export const MAX_INBOUND_TRACKING_NUMBERS = 50;

/**
 * The tracking of an inbound (B-51, ADR-212), all of it at once: the carrier, the day the goods were picked up, the
 * tracking numbers. `null` and an empty list clear what was written. Who writes it follows the incoterm
 * (`inboundTrackingSide`).
 */
export const trackInboundInput = inboundActionInput.extend({
  carrier: text(60).nullable(),
  pickupDate: z.iso.date().nullable(),
  trackingNumbers: z
    .array(text(100))
    .max(MAX_INBOUND_TRACKING_NUMBERS)
    .refine((numbers) => new Set(numbers).size === numbers.length, "each tracking number once"),
});
export type TrackInboundInput = z.input<typeof trackInboundInput>;
export const trackInboundBody = trackInboundInput.omit({ inboundId: true });

/**
 * B-89 (ADR-244): the supplier's side moves the day the goods are ready and changes the pickup contact of an inbound on
 * its way, in every incoterm, all of it at once: `null` clears a field. ADR-323: and the expected delivery at the
 * warehouse, which once submitted moves and is never cleared: absent, it stays as it is (also for a client of before).
 */
export const updateInboundPickupInput = inboundActionInput.extend({
  ...pickupFields,
  expectedDeliveryDate: z.iso.date().optional(),
});
export type UpdateInboundPickupInput = z.input<typeof updateInboundPickupInput>;
export const updateInboundPickupBody = updateInboundPickupInput.omit({ inboundId: true });

/**
 * Why the brand or Logistics sends an inbound back to the supplier, or why it is cancelled: it becomes a note of the
 * inbound. Why Operations forces a failed three-way check: it stays on the check and on its gate (ADR-131).
 */
export const inboundReasonInput = inboundActionInput.extend({ reason: poReason });
export type InboundReasonInput = z.input<typeof inboundReasonInput>;
export const inboundReasonBody = inboundReasonInput.omit({ inboundId: true });

/**
 * What the supplier's side does about the missing units of a received inbound, once (ADR-140): a credit note, a later
 * shipment, or both; with a credit note, its file if it has it already (a file uploaded in the `inbound` folder).
 */
export const settleInboundShortageInput = inboundActionInput.extend({
  resolution: z.enum(missingItemsResolutions),
  fileId: z.uuid().optional(),
});
export type SettleInboundShortageInput = z.input<typeof settleInboundShortageInput>;
export const settleInboundShortageBody = settleInboundShortageInput.omit({ inboundId: true });

/** The brand authorizes what is out of tolerance, with a memo if it wants (`approval_memo`). */
export const authorizeInboundInput = inboundActionInput.extend({ memo: text(2000).optional() });
export type AuthorizeInboundInput = z.input<typeof authorizeInboundInput>;
export const authorizeInboundBody = authorizeInboundInput.omit({ inboundId: true });

/** Newest first. */
export const getInboundHistoryInput = z.object({
  inboundId: z.uuid(),
  ...cursorPagination({ defaultLimit: 50, maxLimit: 100 }),
});
export type GetInboundHistoryInput = z.input<typeof getInboundHistoryInput>;
export const inboundHistoryQuery = getInboundHistoryInput.omit({ inboundId: true });

/** How many files an inbound holds at most: the documents of one shipment. */
export const MAX_INBOUND_DOCUMENTS = 50;

/** The kinds a supplier links in F1: the credit notes come with the outcomes of the receipt (P5.2f). */
export const supplierDocumentKinds = ["packing_list", "supplier_invoice", "waybill", "photo", "other"] as const;

/** The documents a submission asks for (FR-M11-01): the packing list and the invoice of the supplier. */
export const documentsToSubmit = [
  "packing_list",
  "supplier_invoice",
] as const satisfies readonly (typeof supplierDocumentKinds)[number][];

/** Files already uploaded in the `inbound` folder (ADR-069), linked to a draft, all or none. */
export const attachInboundDocumentsInput = inboundActionInput.extend({
  documents: z
    .array(z.object({ fileId: z.uuid(), kind: z.enum(supplierDocumentKinds) }))
    .min(1)
    .max(MAX_INBOUND_DOCUMENTS),
});
export type AttachInboundDocumentsInput = z.input<typeof attachInboundDocumentsInput>;
export const attachInboundDocumentsBody = attachInboundDocumentsInput.omit({ inboundId: true });

export const removeInboundDocumentInput = inboundActionInput.extend({ documentId: z.uuid() });
export type RemoveInboundDocumentInput = z.input<typeof removeInboundDocumentInput>;
export const inboundDocumentParams = removeInboundDocumentInput.pick({ inboundId: true, documentId: true });

export const downloadInboundDocumentInput = z.object({ inboundId: z.uuid(), documentId: z.uuid() });
export type DownloadInboundDocumentInput = z.input<typeof downloadInboundDocumentInput>;

/** The notes of an inbound, oldest first: a conversation. */
export const listInboundNotesInput = z.object({ inboundId: z.uuid() });
export type ListInboundNotesInput = z.input<typeof listInboundNotesInput>;

const noteBody = text(4000);

/** A note on an inbound, with the id from the client (ADR-046). */
export const addInboundNoteInput = z.object({ id: uuidV7, inboundId: z.uuid(), body: noteBody });
export type AddInboundNoteInput = z.input<typeof addInboundNoteInput>;
export const addInboundNoteBody = addInboundNoteInput.omit({ inboundId: true });

/** Only its author changes or deletes a note. */
export const editInboundNoteInput = z.object({ inboundId: z.uuid(), noteId: z.uuid(), version, body: noteBody });
export type EditInboundNoteInput = z.input<typeof editInboundNoteInput>;
export const editInboundNoteBody = editInboundNoteInput.pick({ version: true, body: true });
export const inboundNoteParams = editInboundNoteInput.pick({ inboundId: true, noteId: true });

export const deleteInboundNoteInput = z.object({ inboundId: z.uuid(), noteId: z.uuid(), version });
export type DeleteInboundNoteInput = z.input<typeof deleteInboundNoteInput>;

/** The messages of an inbound to the systems (ADR-137): the ASN to the warehouse, the inbound and the invoice to BC. */
export const inboundMessages = ["I-08", "I-23", "I-24"] as const;
export type InboundMessage = (typeof inboundMessages)[number];

/** Sends one message of the inbound again, as a new revision: after a refusal, or an outcome that never arrived. */
export const resyncInboundInput = inboundActionInput.extend({ message: z.enum(inboundMessages) });
export type ResyncInboundInput = z.input<typeof resyncInboundInput>;
export const resyncInboundBody = resyncInboundInput.omit({ inboundId: true });

export const getInboundSyncInput = z.object({ inboundId: z.uuid() });
export type GetInboundSyncInput = z.input<typeof getInboundSyncInput>;

/** How an inbound stands towards the warehouse and Business Central: one state per message (spec v0.2 §4 rule 9). */
export const inboundSyncDto = z
  .object({
    inboundId: z.uuid(),
    messages: z.array(aggregateSyncDto.extend({ message: z.enum(inboundMessages) })),
    /**
     * The supplier's invoice as the invoices file to the ERP takes it (spec v0.3 §7.4, ADR-256), from the dispatch:
     * null before it.
     */
    erpInvoice: erpInvoiceStateDto.nullable(),
  })
  .meta({ id: "InboundSync" });

/** The supplier's invoice of a dispatched inbound for the invoices file to the ERP (ADR-256). */
export const inboundErpInvoiceInput = z.object({ inboundId: z.uuid() });
export type InboundErpInvoiceInput = z.input<typeof inboundErpInvoiceInput>;

/** Another inbound an inbound names. */
const inboundRef = z.object({ id: z.uuid(), inboundNumber: z.string() });

/** An inbound: in the list, alone and after an action. Dates as `YYYY-MM-DD`, numbers as they are typed. */
export const inboundDto = z
  .object({
    id: z.uuid(),
    inboundNumber: z.string(),
    status: z.enum(inboundStatuses),
    poId: z.uuid(),
    poNumber: z.string(),
    brandOrgId: z.uuid(),
    brand: z.string(),
    supplierOrgId: z.uuid(),
    supplier: z.string(),
    /**
     * The Actor answers for this inbound as its supplier (`actsFor`, the rule of `assertSupplierSide`, ADR-170): always
     * for Tomorrow. Only then it writes, sends, cancels and settles it.
     */
    asSupplier: z.boolean(),
    /** The Actor answers for it as its brand (`assertBrandSide`): only then it authorizes or sends it back. */
    asBrand: z.boolean(),
    /** The warehouse of the PO. */
    destination: codeName.nullable(),
    incoterm: z.string().nullable(),
    currency: z.string().nullable(),
    invoiceNumber: z.string().nullable(),
    invoiceDate: z.iso.date().nullable(),
    invoiceNetTotal: decimal.nullable(),
    invoiceVatAmount: decimal.nullable(),
    invoiceTotal: decimal.nullable(),
    packingListNumber: z.string().nullable(),
    readyDateForPickup: z.iso.date().nullable(),
    pickupContactName: z.string().nullable(),
    pickupPhone: z.string().nullable(),
    pickupEmail: z.string().nullable(),
    expectedDeliveryDate: z.iso.date().nullable(),
    deliveryNumber: z.string().nullable(),
    totalCartons: z.number().int().nullable(),
    totalPallets: z.number().int().nullable(),
    grossWeightKg: decimal.nullable(),
    netWeightKg: decimal.nullable(),
    boxDimensions: z.string().nullable(),
    palletDimensions: z.string().nullable(),
    contentDetails: z.string().nullable(),
    comments: z.string().nullable(),
    /** The tracking of the shipment to the warehouse (B-51, ADR-212): written after the submission. */
    carrier: z.string().nullable(),
    pickupDate: z.iso.date().nullable(),
    trackingNumbers: z.array(z.string()),
    /**
     * Who writes the tracking, by the incoterm (`inboundTrackingSide`): the supplier when it arranges the carriage,
     * Tomorrow when its forwarder does.
     */
    trackingSide: z.enum(inboundTrackingSides),
    /** The sum of the declared quantities of the lines. */
    totalUnits: z.number().int(),
    lineCount: z.number().int(),
    /** Lines out of tolerance, counted at the submission; 0 before it. */
    outOfToleranceCount: z.number().int(),
    documentCount: z.number().int(),
    approvalMemo: z.string().nullable(),
    /**
     * How its messages stand (ADR-137): I-08 to the warehouse, I-23 and I-24 to Business Central. Only Tomorrow reads
     * them (ADR-170).
     */
    asnSyncStatus: z.enum(syncStatuses).optional(),
    bcSyncStatus: z.enum(syncStatuses).optional(),
    invoiceSyncStatus: z.enum(syncStatuses).optional(),
    /** The reading of the packing list by the extraction tool (ADR-233): null if never read. Only Tomorrow reads it. */
    extraction: extractionDto.nullable().optional(),
    submittedAt: z.iso.datetime().nullable(),
    authorizedAt: z.iso.datetime().nullable(),
    /** What the warehouse received against what was declared (ADR-139): null until it expects nothing more. */
    receiptOutcome: z.enum(receiptOutcomes).nullable(),
    /** When Logistics confirmed an outcome with a difference; null before, and for an outcome without one. */
    outcomeConfirmedAt: z.iso.datetime().nullable(),
    /** What the supplier's side chose for the missing units, and when (ADR-140); null before, and without any. */
    missingItemsResolution: z.enum(missingItemsResolutions).nullable(),
    missingItemsResolvedAt: z.iso.datetime().nullable(),
    /**
     * An inbound of the units in more of `parentInbound` (ADR-139): already in the warehouse, on hold until the
     * supplier invoices them; its lines do not change, and it goes to Business Central only.
     */
    isExtraUnits: z.boolean(),
    /**
     * When Logistics released its units on hold (ADR-243): they are free again and Business Central has the receipt
     * (I-23); the inbound waits for the supplier's invoice (I-24). Null otherwise.
     */
    unitsReleasedAt: z.iso.datetime().nullable(),
    parentInbound: inboundRef.nullable(),
    /** The inbound of the units in more of this one, once Logistics confirmed them. */
    extraUnitsInbound: inboundRef.nullable(),
    version: z.number().int(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: "Inbound" });

/** A line of an inbound, with the PO line it carries. */
export const inboundLineDto = z.object({
  id: z.uuid(),
  poLineId: z.uuid(),
  lineNo: z.number().int(),
  skuId: z.uuid(),
  skuCode: z.string().nullable(),
  ean: z.string().nullable(),
  description: z.string().nullable(),
  /** The confirmed quantity of the PO line, or the ordered one. */
  qtyPo: z.number().int(),
  qtyDeclared: z.number().int(),
  shortShipmentAccepted: z.boolean(),
  /** Decided at the submission (V-02); null before it. */
  isWithinTolerance: z.boolean().nullable(),
  qtyReceived: z.number().int(),
  /** With the outcome (ADR-139): the units in more and missing beyond the tolerance of the PO line, else 0. */
  extraUnitsReceived: z.number().int(),
  missingUnits: z.number().int(),
  /**
   * With the outcome (ADR-182): the units of the tolerance of the PO line this line took, the difference it accepted;
   * the inbounds of a PO line share its tolerance, one after the other. 0 before the outcome, or beyond the tolerance.
   */
  toleranceUnitsUsed: z.number().int(),
});

/**
 * The units of an SKU the ASN did not declare, received on an inbound (ADR-138 point 3): in more, on the first line of
 * the PO with that SKU (`poLineNo`), or outside the PO (null), handled outside Nexxt in F1 (ADR-139).
 */
export const undeclaredItemDto = z.object({
  skuId: z.uuid(),
  skuCode: z.string().nullable(),
  qty: z.number().int(),
  poLineNo: z.number().int().nullable(),
});
export type UndeclaredItem = z.output<typeof undeclaredItemDto>;

/** A file of an inbound, with what the procurement worker copied from it to serve it (ADR-084 point 5). */
export const inboundDocumentDto = z.object({
  id: z.uuid(),
  kind: z.enum(inboundDocumentKinds),
  fileId: z.uuid(),
  filename: z.string(),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
  uploadedBy: z.string().nullable(),
  uploadedAt: z.iso.datetime(),
});

/**
 * An inbound with its lines, its documents, the lines of its packing list and its three-way check (ADR-131): the last
 * one, from `in_exception` on; null before it, and while a new packing list waits for its check.
 */
export const inboundDetailDto = z
  .object({
    inbound: inboundDto,
    lines: z.array(inboundLineDto),
    documents: z.array(inboundDocumentDto),
    packingList: z.array(packingListLineDto),
    threeWayCheck: threeWayCheckDto.nullable(),
    /** With an outcome with units in more; empty otherwise. */
    undeclaredItems: z.array(undeclaredItemDto),
    /** The credit note of its missing units, once the supplier's side chose one (ADR-140). */
    creditNote: creditNoteDto.nullable(),
    /** What the supplier's side may choose for its missing units while they wait for it; empty otherwise. */
    missingItemsChoices: z.array(z.enum(missingItemsResolutions)),
  })
  .meta({ id: "InboundDetail" });

/** A line of a confirmed PO an inbound can carry. */
const inboundCandidateDto = z.object({
  poLineId: z.uuid(),
  lineNo: z.number().int(),
  skuId: z.uuid(),
  skuCode: z.string().nullable(),
  ean: z.string().nullable(),
  description: z.string().nullable(),
  qtyPo: z.number().int(),
  /** Units of the line in the inbounds already submitted and not cancelled. */
  qtyAnnounced: z.number().int(),
  /** The units the line may differ by without the brand's authorization (V-02). */
  acceptedUnits: z.number().int(),
});

export const inboundCandidatesDto = z
  .object({ poId: z.uuid(), poNumber: z.string(), currency: z.string(), lines: z.array(inboundCandidateDto) })
  .meta({ id: "InboundCandidates" });

/** A note: the conversation of supplier, brand and Tomorrow on the inbound. */
export const inboundNoteDto = z
  .object({
    id: z.uuid(),
    body: z.string(),
    author: z.string().nullable(),
    /** The Actor wrote it: it may change or delete it. */
    own: z.boolean(),
    createdAt: z.iso.datetime(),
    editedAt: z.iso.datetime().nullable(),
    version: z.number().int(),
  })
  .meta({ id: "InboundNote" });

/** What the deletion of a note answers: its id. */
export const deletedInboundNoteDto = z.object({ id: z.uuid() }).meta({ id: "DeletedInboundNote" });
